// Angular
import { BehaviorSubject, takeUntil } from 'rxjs';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';

// Libraries
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

// Models
import { Channel, ChannelOptions } from '../../modules/channel/models';
import { AppCleanup, DynamicMenuButton, LineStyleOption, LineStyles, LineWeights } from '../../shared/models';
import { UserChannelViewModel } from '../../modules/channel/models/user-channel-view.model';
import { DynamicTableColumn, DynamicTableType } from '../../shared/models/dynamic-table-column.model';

// Services
import { ChannelService } from '../../modules/channel/services';
import { JobService } from '../../modules/job/services/job.service';
import { SpinnerService } from '../../shared/components/spinner/services/spinner.service';

// Helpers
import { UtilityHelper } from '../../shared/helpers';

import { SpinnerSizeType } from '../../shared/components/spinner/spinner.component';
@Component({
	selector: 'user-channel-settings-dialog',
	templateUrl: './user-channel-settings-dialog.component.html',
	styleUrls: ['./user-channel-settings-dialog.component.scss'],
})
export class UserChannelSettingDialogComponent extends AppCleanup implements OnInit, OnDestroy {
  readonly SpinnerSizeType = SpinnerSizeType;
	componentTitle: string = 'User channel settings';
	lineStyleOptions: Array<LineStyleOption> = [...LineStyles.List];
	lineWeightOptions: Array<number> = [...LineWeights.List];
	protected userChannelsVar: BehaviorSubject<UserChannelViewModel[]> = new BehaviorSubject<UserChannelViewModel[]>(null);
	userChannels$ = this.userChannelsVar.asObservable();
	columnList: DynamicTableColumn[] = [
		{
			name: 'channel.label',
			display: 'Channel',
			editable: false,
			width: 4,
		},
		{
			name: 'colorHex',
			display: 'Line Color',
			type: DynamicTableType.COLOR,
			editable: true,
			width: 2,
		},
		{
			name: 'lineStyle',
			display: 'Line Style',
			type: DynamicTableType.LINE_STYLE,
			editable: true,
			width: 3,
		},
		{
			name: 'lineWeight',
			display: 'Line Weight',
			type: DynamicTableType.LINE_WEIGHT,
			editable: true,
			width: 3,
		},
	];

	menuButtons: DynamicMenuButton[] = [
		{
			matIcon: 'close',
			name: 'Delete',
			event: this.deleteUserChannel.bind(this),
		},
	];

	addMenuButtons: DynamicMenuButton[] = [];
	filteredChannels: Channel[] = [];
	selectedUserChannelList: UserChannelViewModel[] = [];
	channelsToDelete: any[] = [];
	dataLoaded: boolean = false;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: {},
		private channelService: ChannelService,
		private jobService: JobService,
		private spinnerService: SpinnerService,
		public dialogRef: MatDialogRef<UserChannelSettingDialogComponent>
	) {
		super();
	}

	ngOnInit() {
		this.subscribeToUserChannel();
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	private subscribeToUserChannel() {
		this.dataLoaded = false;

		this.channelService.userChannels$.pipe(takeUntil(this.destroy)).subscribe((userChannels) => {
			if (userChannels) {
				this.selectedUserChannelList = userChannels.filter((selectedChannel) => selectedChannel.channel.options != ChannelOptions.VeryHidden);
				this.selectedUserChannelList = UtilityHelper.deepCopy(this.selectedUserChannelList); // Copying so that when dialog is closed without saving, the selection doesn't persist
				this.filterChannels();
				this.dataLoaded = true;
			}
		});
	}

	addChannel(channel: Channel) {
		if (!channel) {
			return;
		}

		let selectedChannel: any = {
			id: 0,
			channelId: channel.id,
			channel: channel,
			min: channel.min,
			max: channel.max,
			highDanger: channel.highDanger,
			highWarning: channel.highWarning,
			lowDanger: channel.lowDanger,
			lowWarning: channel.lowWarning,
			precision: channel.precision,
			options: channel.options,
			colorHex: channel.colorHex,
			lineStyle: channel.lineStyle,
			lineWeight: channel.lineWeight,
			displayByDefault: true,
			showGraph: true,
			seq: channel.seq,
			eventsUntilTriggered: 0,
			eventsUntilResolved: 0,
		};

		this.selectedUserChannelList.push(selectedChannel);

		this.filterChannels();
	}

	deleteUserChannel(userChannel: UserChannelViewModel) {
		if (!userChannel) {
			return;
		}

		let foundUserChannel = this.selectedUserChannelList.find((selectedUserChannel) => selectedUserChannel.id === userChannel.id);

		if (!foundUserChannel) {
			return;
		}

		this.channelsToDelete.push({ channelId: userChannel.id, id: foundUserChannel.id });

		let indexToDelete = this.selectedUserChannelList.findIndex((selectedUserChannel) => selectedUserChannel.id === userChannel.id);

		this.selectedUserChannelList.splice(indexToDelete, 1);

		this.filterChannels();
	}

	filterChannels() {
		this.filteredChannels = this.channelService.channels.filter((channel) => channel.options != ChannelOptions.VeryHidden && !this.selectedUserChannelList.find((selectedChannel) => selectedChannel.channelId === channel.id));

		this.addMenuButtons = []; // Resetting buttons
		this.filteredChannels.forEach((channel) => {
			this.addMenuButtons.push({
				matIcon: '',
				name: channel.label,
				event: this.addChannel.bind(this, channel),
			});
		});

		this.selectedUserChannelList = this.selectedUserChannelList.sort((left, right): number => {
			return left.channel.label.toLowerCase().localeCompare(right.channel.label.toLowerCase());
		});

		this.userChannelsVar.next(this.selectedUserChannelList);
	}

	submit() {
		this.spinnerService.showAppSpinner();
		this.channelService.updateUserDefaultChannels({ userJobChannels: this.selectedUserChannelList, userJobChannelsToDelete: this.channelsToDelete }).subscribe(
			(updatedUserChannels) => {
				this.channelService.modifyUserChannelRecords(updatedUserChannels);
				this.jobService.updateJobsCacheChannels(updatedUserChannels);
				this.spinnerService.hideAppSpinner();
				// TODO by Isaac add success and error messages
			},
			(error) => {
				this.spinnerService.showAppSpinner();
			}
		);

		this.dialogRef.close();
	}
}
